import * as React from 'react';
import appConfig from 'local_config';
import ColorsVariantsSelector from 'bom/components/colorsVariantsSelector/colorsVariantsSelector';
import { parseDataForBomSave } from 'bom/bomHelpers';
import { SET_NEW_PART_QUANTITY, SAVE_BOM_FOR_AURORA, SAVE_BOM_FOR_OS } from 'actions';
import { Table, TableHead, TableBody, TableRow, TableCell, TextField, Tooltip, Typography, Box, TableContainer, Paper } from '@mui/material';
import { saveBomAndDownloadPdf } from '../../utils/saveBom';
import { products } from '__common/constants/products';
import { sortBomTableByUniracRequest } from './utils/bomTableSorting';
import { dispatch, state } from '__common/store';
import { isDomesticPart, PARTS_BELONG_FOR_PACK_NOTE, partToPackMap, S5_ATTACHMENT_PARTS, SM_168_PARTS, SM_208_PARTS } from '__common/constants/parts';
import { applyDomesticContent } from '__common/utils/versionCompare/versionCompare';
import { bomPart, bomPartsList } from '../../../../../decls/bom/parts';

type Props = {
  onChangeQuantity?: Function;
  preview?: boolean;
  productId?: string;
  partsList: bomPartsList;
  dispatch: Function,
  projectId: string,
};

export default class BomTable extends React.Component<Props, {}> {
  imageX: number;
  imageY: number;
  partImageName: string;
  showImage = false;

  sendBomToTheAurora = () => {
    const{projectConfiguration:{projectId, productId}, bom: { partsList, extraParameters },railsSelector: { railsList, selectOnlySMHDRails }} = state();
    const bomSaveRequestBody = parseDataForBomSave(partsList, railsList, extraParameters, products[productId], selectOnlySMHDRails);
    if (bomSaveRequestBody && Object.keys(bomSaveRequestBody)?.length > 0) {
      // Post BOM JSON to parent window for external consumer
      try {
        if (window?.parent) {
          window.parent.postMessage({ type: 'UBUILDER_BOM_READY', payload: bomSaveRequestBody }, '*');
        }
      } catch (e) {
        // swallow postMessage errors silently
      }
      dispatch(SAVE_BOM_FOR_AURORA(productId.toString(), projectId, bomSaveRequestBody, true));
    }
  }

  sendBomToTheOs = () => {
    const{projectConfiguration:{ productId, projectId }, bom: { partsList, extraParameters },railsSelector: { railsList, selectOnlySMHDRails }} = state();
    const bomSaveRequestBody = parseDataForBomSave(partsList, railsList, extraParameters, products[productId], selectOnlySMHDRails);
    if (bomSaveRequestBody && Object.keys(bomSaveRequestBody)?.length > 0) {
      try {
        if (window?.parent) {
          window.parent.postMessage({ type: 'UBUILDER_BOM_READY', payload: bomSaveRequestBody }, '*');
        }
      } catch (e) {}
    }
    dispatch(SAVE_BOM_FOR_OS(productId.toString(), projectId, bomSaveRequestBody, true));
    // saveBomAndDownloadPdf();
  }

  printTable = () => {
    // Build BOM JSON and post to parent before triggering PDF download
    const{projectConfiguration:{ productId }, bom: { partsList, extraParameters },railsSelector: { railsList, selectOnlySMHDRails }} = state();
    const bomSaveRequestBody = parseDataForBomSave(partsList, railsList, extraParameters, products[productId], selectOnlySMHDRails);
    if (bomSaveRequestBody && Object.keys(bomSaveRequestBody)?.length > 0) {
      try {
        if (window?.parent) {
          window.parent.postMessage({ type: 'UBUILDER_BOM_READY', payload: bomSaveRequestBody }, '*');
        }
      } catch (e) {}
    }
    saveBomAndDownloadPdf();
  }

  tableTitle() {
    const { projectConfiguration } = state();
    return (
      <Box className="table-title" sx={{ 
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        paddingBottom: 0,
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '3px solid #003B5C',
        background: 'linear-gradient(90deg, rgba(0, 59, 92, 0.02) 0%, rgba(0, 59, 92, 0.05) 100%)',
        borderRadius: '8px 8px 0 0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Typography variant="inherit" component="h2" sx={{ 
            fontFamily: 'Oswald',
            fontWeight: 500, 
            color: '#12232d',
            marginBottom: 0,
            fontSize: '22px',
            lineHeight: '22px',
            letterSpacing: '0.05em'
          }}>
            Complete part list
          </Typography>
          
          <Box className="table-title-legend" sx={{ display: 'flex', alignItems: 'center', gap: 3, marginLeft: 2 }}>
            <Typography variant="inherit" sx={{ 
              fontFamily: 'Oswald',
              fontWeight: 100, 
               color: '#12232d',
              fontSize: '15px',
              lineHeight: '15px',
              display: 'flex',
              alignItems: 'center',
              letterSpacing: '0.05em'
            }}>
              LEGEND
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ 
                width: 16, 
                height: 16, 
                backgroundColor: '#CF5232', 
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(207, 82, 50, 0.3)'
              }} />
              <Typography variant="inherit" sx={{ 
              fontFamily: 'Oswald',
              fontWeight: 100, 
               color: '#12232d',
              fontSize: '15px',
              lineHeight: '15px',
              display: 'flex',
              alignItems: 'center',
              letterSpacing: '0.05em'
              }}>
                Base System Part
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ 
                width: 16, 
                height: 16, 
                backgroundColor: '#0A3C5D', 
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(10, 60, 93, 0.3)'
              }} />
              <Typography variant="inherit" sx={{ 
              fontFamily: 'Oswald',
              fontWeight: 100, 
               color: '#12232d',
              fontSize: '15px',
              lineHeight: '15px',
              display: 'flex',
              alignItems: 'center',
              letterSpacing: '0.05em'
              }}>
                Accessory
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box>
          {projectConfiguration?.is_aurora_project && (
            <Tooltip title="Export BOM data to Aurora">
              <Box 
                onClick={this.sendBomToTheAurora} 
                className="table-title-print"
              >
                <img src={require("assets/media/icons/send_data_to_aurora.png")} alt='Send Data to Aurora' height={20} width={20} />
                <Typography variant="button" sx={{ 
                  fontSize: '0.8rem', 
                  fontWeight: 700,
                  letterSpacing: '0.5px'
                }}>
                  EXPORT TO AURORA
                </Typography>
              </Box>
            </Tooltip>
          )}
          {projectConfiguration?.is_os_project && (
          <div onClick={this.sendBomToTheOs} className="table-title-print">
            <img src={require("assets/media/icons/send_bom_to_opensolar.png")} height={35} width={35}/>
            <div>EXPORT TO OPENSOLAR</div>
          </div>
        )}
        {!(projectConfiguration?.is_aurora_project || projectConfiguration?.is_os_project) && (
            <Tooltip title="Print or download BOM as PDF">
              <Box
                onClick={this.printTable}
                className="table-title-print"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  padding: 0,
                  backgroundColor: 'transparent',
                  background: 'none',
                  border: 'none',
                  boxShadow: 'none',
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    background: 'none',
                  },
                }}
              >
                <i className="material-icons" style={{ fontSize: '20px', lineHeight: 1 }}>print</i>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: 'Oswald',
                    fontSize: '19px',
                    fontWeight: 500,
                    lineHeight: '19px',
                    letterSpacing: '0.05em',
                    backgroundColor: 'transparent',
                  }}
                >
                  PRINT THIS LIST
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  }


  tableHeader() {
    return (
      <TableHead className="part-list-table-header">
        <TableRow>
          <TableCell>Part Number</TableCell>
          <TableCell>Part Type</TableCell>
          <TableCell>Description</TableCell>
          <TableCell>Quantity</TableCell>
          <TableCell><span>Suggested</span><span>Quantity</span></TableCell>
          <TableCell><span>Unit</span><span>Price (USD)</span></TableCell>
          <TableCell><span>Total List</span><span>Price (USD)</span></TableCell>
        </TableRow>
      </TableHead>
    );
  }


  tableBody() {
    const { preview } = this.props;

    if (preview) {
      return (
        <TableBody>
          {this.tableRows()}
        </TableBody>
      );
    }

    return (
      <TableBody>
        {this.tableRows()}
        {this.tableTotalRow()}
      </TableBody>
    );
  }


  showPartImage = (partImage, e) => {
    this.imageX = e.clientX + 10
    this.imageY = e.clientY
    this.partImageName = partImage;
    this.showImage = true;
    this.forceUpdate();
  }

  hidePartImage = () => {
    this.showImage = false;
    this.imageX = null;
    this.imageY = null;
    this.partImageName = null;
    this.forceUpdate();
  }

  validatePartQuantity = (partNumber, qty) => {
    const { dispatch } = this.props;

    if (qty > 1000000) {
      dispatch(SET_NEW_PART_QUANTITY(partNumber, qty));
    }

  }

  partQuantityColumn(part: bomPart) {
    const { preview } = this.props;

    if (preview) {
      return part.quantity || 0;
    }

    return (
      <TextField
        id="part-quantity"
        className="part-quantity"
        variant="standard"
        value={part.quantity}
        onBlur={this.validatePartQuantity.bind(this, part.number, part.quantity)}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.props.onChangeQuantity(part.number, event.target.value)}
        type="number"
        size="small"
        onKeyDown={(e) => {
          if (e.key === '-' || e.key === '+' || e.key === '.' || e.key === 'e' || e.key === 'E') {
            e.preventDefault()
          }
        }}
        inputProps={{
          min: 0,
        }}
      />
    );
  }

  dynamicSort = (property) => {
    let newProperty = property;
    let sortOrder = -1;
    if (property[0] === '-') {
      sortOrder = -1;
      newProperty = newProperty.substr(1);
    }
    return function (a, b) {
      const result =
        a[newProperty] < b[newProperty]
          ? -1
          : a[newProperty] > b[newProperty]
            ? 0
            : 1;
      return result * sortOrder;
    };
  }

  makePartTypeClassFriendly(className) {
    return className
      .split(' ')
      .join('-')
      .split('.')
      .join('-')
      .toLocaleLowerCase();
  }

  tableRows() {
    const{projectConfiguration} = state();
    const uanchorList = projectConfiguration?.projectEnvConfig?.uanchor_part_number || [];
    const isDomesticSelected = projectConfiguration.projectEnvConfig.domestic_content || projectConfiguration.projectEnvConfig.is_domestic_mlpe_required;
    const { partsList, productId, preview } = this.props;
    let newPartList;
    if (partsList && partsList.length > 0) {
      newPartList = partsList.filter((elem, index, self) => self.findIndex(
        ele => ele.number === elem.number) === index);
    };
    return sortBomTableByUniracRequest(products[productId], newPartList)
      .map(
        (part: any, index) =>
          !part.disabled && (
            <TableRow key={this.makePartTypeClassFriendly(part.number)} className={`part-row part-row-${this.makePartTypeClassFriendly(part.number)}`}>
              <TableCell className="part-number-and-image" style={{ position: 'relative' }}>
                <div className="part-number-and-image-content">
                  <div style={{ whiteSpace: 'pre-line' }} className={`part-number ${this.makePartTypeClassFriendly(part.number)}`}>{part.number.replace('6 (U', '6 \n  (U')}</div>
                  {S5_ATTACHMENT_PARTS.includes(part.number) || !part.image || part.image.includes('default.jpg') || part.number === 'RAYTV3TOOL' ? null : <img
                    alt="part_image"
                    className="part-image"
                    src={`${location.protocol}//${appConfig.apiUrl.slice(0, -1)}${part.image}`}
                    onMouseOver={this.showPartImage.bind(null, part.image)}
                    onMouseLeave={this.hidePartImage}
                  />
                }
                </div>
              </TableCell>
              <TableCell
                className={
                  part.category === 'Base'
                    ? `base-part ${this.makePartTypeClassFriendly(part.type)} ${this.makePartTypeClassFriendly(part.type)}-${this.makePartTypeClassFriendly(part.number)}`
                    : `accessory-part ${this.makePartTypeClassFriendly(part.type)} ${this.makePartTypeClassFriendly(part.type)}-${this.makePartTypeClassFriendly(part.number)}`
                }
              >
                {part.type}
              </TableCell>
              <TableCell
                className={`md-cell--middle desc-and-color${
                  part.variants && !preview && part.variants.length > 0 && products[productId] !== 12
                    ? ' has-color-variant'
                    : ''
                }`}
              >
                <Tooltip
                  title={part.description.length > 100 ? part.description : ''}
                  disableHoverListener={part.description.length <= 100}
                  arrow
                  placement="top"
                >
                  <div className="part-description-content">
                    <span style={{ display: 'inline-block', paddingTop: '7px' }}>
                    {part.description.length > 100
                      ? `${part.description.substring(0, 100).toUpperCase()}...`
                      : part.description.toUpperCase()}
                    {SM_168_PARTS.includes(part.number) ? (<span className='part-number-warning'> - CONFIRM AVAILABILITY<br/> WITH YOUR DISTRIBUTOR, 168" LENGTH IS <br/>IS BEING DISCONTINUED - TRANSITIONING<br/> TO 185" LENGTH</span>) : null}
                    {SM_208_PARTS.includes(part.number) ? (<span className='part-number-warning'> - CONFIRM AVAILABILITY<br/> WITH YOUR DISTRIBUTOR, 208" LENGTH IS <br/>IS BEING DISCONTINUED - TRANSITIONING<br/> TO 185" LENGTH</span>) : null}

                    {(uanchorList && uanchorList.includes(part.number))? <p style={{ margin:'10px 0 5px 0', color: "#CF5232", fontSize: "10px", lineHeight: 1 }}>Contact Unirac for Pricing</p> : null}

                    {PARTS_BELONG_FOR_PACK_NOTE.some(
                      (partItem) => partItem.partNumber === part.number
                    ) && (
                        <span className="part-description-note">
                          <strong>
                            {' '}
                            - Minimum Pack Size Quantities of {partToPackMap[part.number]}.
                          </strong>
                        </span>
                      )}
                    {part.domestic &&
  !isDomesticSelected &&
  ![2, 9, 11, 14, 15, 19, 20, 24, 25, 26, 27, 28, 30, 31, 32, 35, 36, 34, 99].includes(products[productId]) &&
  !(products[productId] === 33 &&
    applyDomesticContent(projectConfiguration.projectVersion)) && (
                      <p
                        className="bom-domestic-note"
                        style={{
                          margin: '10px 0 5px 0',
                          color: '#CF5232',
                          fontSize:'13px',
                          lineHeight:'13px'
                        }}
                      >
                        US-manufactured part available. For guaranteed domestic content, add
                        -US to the end of the part number.
                      </p>
                    )}
                {(S5_ATTACHMENT_PARTS.includes(part.number)) ? <p style={{ margin:'10px 0 5px 0', color: "#CF5232", fontSize: "10px", lineHeight: 1 }}>Not supplied through Unirac, please contact your supplier for S-5! components.</p> : null}
                    {[2, 9, 11, 14, 15, 19, 20, 24, 28, 30, 31, 32, 35, 33, 34, 36, 25, 26, 27, 99].includes(products[productId]) &&
                      isDomesticPart(part.number) &&
                      isDomesticSelected && (
                      <p
                        className="bom-domestic-note"
                        style={{
                          margin: '10px 0 5px 0',
                          color: '#CF5232',
                          fontSize: '13px',
                          lineHeight: '13px'
                        }}
                      >
                        Domestic Content.
                      </p>
                    )}
                    </span>
                  </div>
                </Tooltip>
                {part.variants &&
                  !preview &&
                  part.variants.length > 0 &&
                  products[productId] !== 12 && (
                    <div className="color-variant-control">
                      <ColorsVariantsSelector
                        partToReplace={part}
                        variants={part.variants}
                      />
                    </div>
                  )}
              </TableCell>

              <TableCell className="part-quantity-cell">{this.partQuantityColumn(part)}</TableCell>
              <TableCell className="part-suggested-quantity">{part.suggested_quantity}</TableCell>

              <TableCell className="part-price">{(uanchorList && uanchorList.includes(part.number)) ? '0.00' : Number(part.price).toFixed(2)}</TableCell>
              <TableCell className="part-total-price">{(uanchorList && uanchorList.includes(part.number)) ? '0.00' : Number(part.price * part.quantity).toFixed(2)}</TableCell>
            </TableRow>
          ),
      );
  }

  tableTotalRow() {
    const { projectConfiguration } = state();
    const uanchorList = projectConfiguration?.projectEnvConfig?.uanchor_part_number || [];
    const uniqueParts = (this.props.partsList || []).filter(
      (part, index, parts) => parts.findIndex(candidate => candidate.number === part.number) === index,
    );
    const totalPrice = uniqueParts.reduce((total, part) => {
      if (part.disabled || uanchorList.includes(part.number)) {
        return total;
      }

      return total + (Number(part.price) || 0) * (Number(part.quantity) || 0);
    }, 0);

    return (
      <TableRow key="total_row" className="bom-total-row">
        <TableCell colSpan={6} className="bom-total-label">
          <Typography component="span">
            TOTAL PRICE (USD) -
          </Typography>
        </TableCell>
        <TableCell className="bom-total-value">
          <Typography component="span">
            {totalPrice.toFixed(2)}
          </Typography>
        </TableCell>
      </TableRow>
    );
  }

  partImage() {
    let imageDiv;
    if (this.showImage && this.partImageName && !this.partImageName.includes('default.jpg')) {
      imageDiv = (
        <div
          className="preview-image-box"
          style={{ position: 'fixed', top: this.imageY, left: this.imageX }}
        >
          <img
            alt="part_image"
            src={`${location.protocol}//${appConfig.apiUrl.slice(0, -1)}${this.partImageName}`}
          />
        </div>
      );
    } else {
      imageDiv = null;
    }

    return imageDiv;
  }

  render() {
    return (
      <div className="bom_table">
        {this.tableTitle()}
        <Box className="bom-table-surface">
          <TableContainer 
            component={Paper} 
            elevation={0}
          >
            <Table 
              className="bom_table_data" 
              size="small" 
              stickyHeader
            >
              {this.tableHeader()}
              {this.props.partsList?.length ? this.tableBody() : null}
            </Table>
          </TableContainer>
        </Box>
        {this.partImage()}
      </div>
    );
  }
}
